/**
 * @file Simple Events wrapper that allows the removal of all events added using the wrapper in a single method call.
 *
 * Useful for scenarios where many event listeners are set, and you want to make sure they are all removed correctly
 * whilst being resistant to future maintenance.
 */

import {array_each} from 'lib/utilities';
import Bus, {Callable} from 'lib/bus';

// Useful aliases for TS Event-related types/interfaces
type EventNameMap = GlobalEventHandlersEventMap
    & WindowEventMap
    & HTMLElementEventMap
    & HTMLBodyElementEventMap
    & DocumentAndElementEventHandlersEventMap
    & ElementEventMap;

type EventName = keyof EventNameMap;

type EventArgType<K extends EventName | string> = K extends EventName
    ? EventNameMap[K]
    : Event;

// Dynamic handler type to get correct inference of the event argument of the listener
type NodeListener<K extends EventName | string> = (event: EventArgType<K>) => any;

type EventOptions = {
    capture?: boolean;
    passive?: boolean;
};

// Use for managing/storing added events
// interface EventNode<E extends EventName | string> {
//     addEventListener(event: E, listener: NodeListener<E>, options?: EventOptions);
//     removeEventListener(event: E, listener: NodeListener<E>, options?: EventOptions);
// }

type EventNode = EventTarget;

type BusTypes<B extends Bus> = ReturnType<B['getTypes']>;
type BusEventName<B extends Bus> = keyof BusTypes<B>;
type BusListener<B extends Bus, E extends BusEventName<B>> = Callable<BusTypes<B>[E]>;

type NodeEventEntry = [EventNode, string, NodeListener<string>, EventOptions | undefined];
type BusEventEntry = [Bus, string, BusListener<Bus, string>];
type EventEntry = NodeEventEntry | BusEventEntry;

export default class Events {
    private readonly events: EventEntry[] = [];

    on<E extends EventName | string>(node: EventNode | ArrayLike<EventNode>, event: E | E[], listener: NodeListener<E>, options?: EventOptions) {
        if (Events.isEventNode(node)) {
            if (typeof event === 'string') {
                node.addEventListener(event, listener, options);

                this.push([node, event, listener, options]);
            } else {
                array_each(event, item => this.on(node, item, listener, options));
            }
        } else {
            array_each(node, item => this.on(item, event, listener, options));
        }
    }

    off<E extends EventName | string, N extends EventNode>(node: N | ArrayLike<N>, event: E | E[], listener: NodeListener<E>) {
        if (Events.isEventNode(node)) {
            if (typeof event === 'string') {
                const index = this.findEntry([node, event, listener]);

                if (index !== -1) {
                    Events.removeNodeEvent(this.events[index] as NodeEventEntry);

                    this.removeEntry(index);
                }
            } else {
                array_each(event, item => this.off(node, item, listener));
            }
        } else {
            array_each(node, item => this.off(item, event, listener));
        }
    }

    onBus<B extends Bus, E extends BusEventName<B>>(bus: B | ArrayLike<B>, event: E | E[], listener: BusListener<B, E>) {
        if (bus instanceof Bus) {
            if (typeof event !== 'object') {
                bus.on(event as string, listener);

                this.push([bus, event as string, listener]);
            } else {
                array_each(event, item => this.onBus(bus, item, listener));
            }
        } else {
            array_each(bus, item => this.onBus(item, event, listener));
        }
    }

    offBus<B extends Bus, E extends BusEventName<B>>(bus: B | ArrayLike<B>, event: E | E[], listener: BusListener<B, E>) {
        if (bus instanceof Bus) {
            if (typeof event !== 'object') {
                const index = this.findEntry([bus, event, listener]);

                if (index !== -1) {
                    Events.removeBusEvent(this.events[index] as BusEventEntry);

                    this.removeEntry(index);
                }
            } else {
                array_each(event, item => this.offBus(bus, item, listener));
            }
        } else {
            array_each(bus, item => this.offBus(item, event, listener));
        }
    }

    offAll(): void {
        for (const entry of this.events) {
            if (Events.isBusEvent(entry)) {
                Events.removeBusEvent(entry);
            } else {
                Events.removeNodeEvent(entry);
            }
        }

        this.events.splice(0, this.events.length);
    }

    private push(entry: EventEntry) {
        this.events.push(entry);
    }

    private removeEntry(index: number) {
        this.events.splice(index, 1);
    }

    private findEntry(matchTo: any[]): number {
        for (let i = this.events.length - 1; i >= 0; --i) {
            const entry = this.events[i];

            if (Events.entryMatches(entry, matchTo)) {
                return i;
            }
        }

        return -1;
    }

    private static entryMatches(entry: EventEntry, matchTo: any[]): boolean {
        for (let m = 0; m < matchTo.length; ++m) {
            if (entry[m] !== matchTo[m]) {
                return false;
            }
        }

        return true;
    }

    private static removeNodeEvent([node, event, listener, options]: NodeEventEntry) {
        node.removeEventListener(event, listener, options);
    }

    private static removeBusEvent([bus, event, listener]: BusEventEntry) {
        bus.off(event, listener);
    }

    private static isNodeEvent(entry: EventEntry): entry is NodeEventEntry {
        return entry.length === 4;
    }

    private static isBusEvent(entry: EventEntry): entry is BusEventEntry {
        return entry.length === 3;
    }

    private static isEventNode<E extends EventName | string = string>(node: any): node is EventNode {
        return node && typeof node === 'object' && typeof node.addEventListener === 'function';
    }
}
